import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Logger } from '../global/logger';
import { GetObjectResponse } from '../global/responses/get-object-response';
import { Grant } from './grant';
import { PermissionEnum } from './permission-enum';
import { WebServiceResponse } from '../global/responses/web-service-response';
import { GrantCompany } from './grant-company';
import { ExecuteObservable } from '../global/execute-observable';

/**
 * This class is a service used to communicate feedback data to the web service.
 */
@Injectable()
export class GrantService {

// #region [ Fields ]

	/** The path to the quote controller. */
	private static readonly GrantControllerPath: string = "Grant/";

	/** Holds the grants which have been accepted by the user. This field will be null if the client hasn't downloaded the grants yet. */
	private _AcceptedGrants: Grant[] = null;

// #region [ Constructor ]

	constructor(
		private _HttpClient: HttpClient
	) { }

// #region [ Properties ]

	public get AcceptedGrants(): Grant[] {
		return this._AcceptedGrants;
	}

// #region [ Public Methods ]

	/**
	 * Clears all state from the service so that data needs to be reloaded.
	 */
	public ClearState(): void {
		this._AcceptedGrants = null;
	}

	/**
	 * Uses the grants loaded into memory to get a list of companies which this user has accepted grants for.
	 *
	 * @param UserGrants The grants to get the company information from.
	 * @param GrantType The grant type used to filter the grants to get the company information from.
	 * @returns An array of companies which the user has grants for.
	 */
	public GetGrantCompanies(UserGrants: Grant[], GrantType: PermissionEnum): GrantCompany[] {
		let objCompanies: GrantCompany[];
		let objFindCompanies: GrantCompany[];

		objCompanies = new Array<GrantCompany>();
		UserGrants.forEach((objGrant: Grant) => {
			if (objGrant.Permission == GrantType) {
				objFindCompanies = objCompanies.filter(objC => objC.CrownIdentifier == objGrant.CrownIdentifier);
				if (objFindCompanies.length == 0)
					objCompanies.push({
						CompanyName: objGrant.CompanyName,
						CrownIdentifier: objGrant.CrownIdentifier
					});
			}
		});
		objCompanies.sort((objA, objB) => (objA.CompanyName > objB.CompanyName ? 1 : -1));

		return objCompanies;
	}

	/**
	 * Gets accepted user grants.
	 *
	 * @returns A promise that supplies the grants.
	 */
	public GetAcceptedGrants(): Promise<Grant[]> {
		let objObservable: Observable<GetObjectResponse<Grant[]>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + GrantService.GrantControllerPath + "GetAcceptedGrants";
		objObservable = this._HttpClient.get<GetObjectResponse<Grant[]>>(strUrl, { withCredentials: true });

		// Setup logging and process response.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<Grant[]>) => {
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
				if (objResponse.OperationSucceeded)
					this._AcceptedGrants = objResponse.Result;
			}
		));

		return new Promise<Grant[]>((objResolve: Function, objReject: Function) => {
			ExecuteObservable.ExecuteQuery<Grant[]>(objObservable, "GrantService.GetAcceptedGrants")
				.then((objGrants: Grant[]) => {
					this.TranslatePermissions(objGrants);
					objResolve(objGrants);
				})
				.catch(() => objReject());
		});
	}

	/**
	 * Returns the grants already loaded in memory or gets them from the server if they haven't been previously loaded.
	 *
	 * @returns A promise that supplies the accepted grants.
	 */
	public LoadAcceptedGrants(): Promise<Grant[]> {
		return new Promise((objResolve: Function, objReject: Function) => {
			if (this.AcceptedGrants == null) {
				this.GetAcceptedGrants()
					.then((objGrants: Grant[]) => objResolve(objGrants))
					.catch(() => objReject());
			}
			else
				objResolve(this.AcceptedGrants);
		});
	}

	/**
	 * Gets issued user grants.
	 *
	 * @returns A promise that supplies the grants.
	 */
	public GetIssuedGrants(): Promise<Grant[]> {
		let objObservable: Observable<GetObjectResponse<Grant[]>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + GrantService.GrantControllerPath + "GetIssuedGrants";
		objObservable = this._HttpClient.get<GetObjectResponse<Grant[]>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<Grant[]>) => {
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
			}
		));

		return new Promise<Grant[]>((objResolve: Function, objReject: Function) => {
			ExecuteObservable.ExecuteQuery<Grant[]>(objObservable, "GrantService.GetIssuedGrants")
				.then((objGrants: Grant[]) => {
					this.TranslatePermissions(objGrants);
					objResolve(objGrants);
				})
				.catch(() => objReject());
		});
	}

	/**
	 * Rejects an issued user grant.
	 *
	 * @param CrownIdentifier The Crown ID of the issuing company.
	 * @param Permission The permission of the issue grant(s).
	 * @returns A promise which rejects the grants.
	 */
	public RejectGrants(CrownIdentifier: string, Permission: PermissionEnum): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + GrantService.GrantControllerPath + "RejectGrants";
		strUrl += `/${CrownIdentifier}/${Permission}`;
		objObservable = this._HttpClient.put<WebServiceResponse>(strUrl, null, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: WebServiceResponse) => {
				Logger.ServiceEndLog(intIdentifier, "PUT", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "GrantService.RejectGrants");
	}

	/**
	 * Accepts an issued user grant.
	 *
	 * @param CrownIdentifier The Crown ID of the issuing company.
	 * @param Permission The permission of the issue grant(s).
	 * @returns A promise which accepts the grants.
	 */
	public AcceptGrants(CrownIdentifier: string, Permission: PermissionEnum): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Set the accepted grants to null so that the client has to download the newest list.
		this._AcceptedGrants = null;

		// Build the request.
		strUrl = environment.WebServiceUrl + GrantService.GrantControllerPath + "AcceptGrants";
		strUrl += `/${CrownIdentifier}/${Permission}`;
		objObservable = this._HttpClient.put<WebServiceResponse>(strUrl, null, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("PUT", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: WebServiceResponse) => {
				Logger.ServiceEndLog(intIdentifier, "PUT", strUrl, objResponse);
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "GrantService.AcceptGrants");
	}

// #region [ Private Methods ]

	/**
	 * Translates the permissions sent from the server (come down as string) to enumeration values.
	 *
	 * @param Grants The grants fetched from the web service.
	 */
	private TranslatePermissions(Grants: Grant[]): void {
		Grants.forEach(objGrant => {
			if (objGrant.Permission.toString() == "TrackShipments")
				objGrant.Permission = PermissionEnum.TrackShipments;
			else if (objGrant.Permission.toString() == "QuoteShipments")
				objGrant.Permission = PermissionEnum.QuoteShipments;
			else if (objGrant.Permission.toString() == "BookQuotes")
				objGrant.Permission = PermissionEnum.BookQuotes;
		});
	}

}